import { useQuery } from '@tanstack/react-query';
import React, { FC, useEffect, useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { CustomPageContext, PageTabs } from '~/app/Layout';
import { ROUTE } from '~/app/routes';
import { InlineCopyInput, Loading, PageHeader } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { CardTransaction, CardTransactionRepository } from '~/repositories/CardTransactionRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner, Line, Text } from '~/styles';

interface Params {
  id: string;
}

export interface CardTransactionProps {
  cardTransaction: CardTransaction;
}

const HeaderRow = styled(Flex)`
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const InlineCopyInputWrapper = styled.div`
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    width: auto;
  }
`;

export const PageCardTransaction: FC<CustomPageContext> = ({ entries }) => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const { addDangerNotification } = useNotificationStore();
  const { id } = useParams<keyof Params>() as Params;

  const {
    data: cardTransaction,
    isLoading,
    error: cardTransactionError,
  } = useQuery({
    queryKey: ['card-transaction', id, currentPlatform?.id, isSandbox],
    queryFn: () => CardTransactionRepository.get(id!),
    enabled: !!id,
  });

  useEffect(() => {
    if (cardTransactionError) {
      navigate(ROUTE.CARD_TRANSACTIONS);
      addDangerNotification({
        content: (cardTransactionError as any).message || 'Failed to load card transaction',
      });
    }
  }, [cardTransactionError, navigate, addDangerNotification]);

  const outletContext = useMemo(() => (cardTransaction ? { cardTransaction } : undefined), [cardTransaction]);

  return (
    <Loading isLoading={isLoading} fullHeight>
      <PageHeader
        text="Card Transactions"
        backPath={ROUTE.CARD_TRANSACTIONS}
        tabs={<PageTabs entries={entries} />}
        backButton
      />
      {id && cardTransaction && (
        <>
          <Inner pt={12} px="var(--page-margin)" pb={16}>
            <Flex flexDirection="column" gap={12}>
              <HeaderRow>
                <Text as="h2" size={16} letterSpacing="tight" weight="medium" color="foreground">
                  Card Transaction
                </Text>

                <InlineCopyInputWrapper>
                  <InlineCopyInput
                    label="Card Transaction ID"
                    value={cardTransaction.id}
                    minWidth="300px"
                    isMonospaced
                  />
                </InlineCopyInputWrapper>
              </HeaderRow>
            </Flex>
          </Inner>
          <Line />
        </>
      )}
      {outletContext && <Outlet context={outletContext} />}
    </Loading>
  );
};
